// Cookie Scanning - scan trust, Active Scans enrollment, category override, unclassified consequence, failed-scan diagnosis.

import {
  BULLET_VARIANTS,
  FAQ_BLOCK_TYPES,
  FAQ_SECTION_ICON_TONES,
} from 'src/services/consts/faq/content.const'

/** @type {import('src/services/helpers/faq/content.helper').FaqTopic} */
export const COOKIE_SCANNING_TOPIC = {
  id: 'cookie-scanning',
  title: 'Cookie Scanning',
  icon: 'scan-search',
  color: 'cta-dark',
  fill: 'cta-light',
  description:
    'Scan a site and decide whether the results are complete enough to act on. Classify cookies and recover from a failed crawl.',
  sections: [
    {
      title: 'Run a scan',
      items: [{ articleId: 'run-a-scan', icon: 'rocket' }],
    },
    {
      title: 'While scans run',
      items: [{ articleId: 'active-scans-in-drawer', icon: 'loader' }],
    },
    {
      title: 'Results and classification',
      items: [
        { articleId: 'classify-cookies', icon: 'list-checks' },
        { articleId: 'unclassified-cookies', icon: 'alert-triangle' },
      ],
    },
    {
      title: 'Troubleshooting',
      items: [{ articleId: 'fix-a-failed-scan', icon: 'wrench' }],
    },
  ],
}

/** @type {Record<string, import('src/services/helpers/faq/content.helper').FaqArticle>} */
export const COOKIE_SCANNING_ARTICLES = {
  'run-a-scan': {
    id: 'run-a-scan',
    title: 'When you can trust a scan',
    description:
      'Only a completed full scan shows every tracker on your site. A preview, a running scan, a filtered view, or a failed scan can look blank even when trackers are there.',
    keywords: [
      'cookies and trackers',
      'cookie discovery',
      'spider',
      'quick scan',
      'empty results',
      'no cookies detected',
      'no trackers detected',
      'filtered empty',
      'scan stuck',
      'already scanning',
      'pages behind login',
      'lazy loaded',
      'CMP accept',
      'consent click',
      'indexeddb',
      'website domain locked',
      'website url locked',
      'scheduled scan',
      '20th',
    ],
    lastUpdated: '2026-10-18',
    content: [
      {
        id: 'ras-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'You can trust the tracker list only after a full scan finishes. Until then, several things can leave it looking blank, and each one needs a different next move.',
      },
      {
        id: 'ras-video-section',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Walkthrough',
        icon: 'file-video',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'ras-video',
            type: FAQ_BLOCK_TYPES.VIDEO,
            videoId: '1223430871',
            hash: 'cb3bf9f177',
            title: 'Scanning and classification walkthrough',
          },
        ],
      },
      {
        id: 'ras-which',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'When scans start',
        icon: 'scan-search',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'ras-which-start',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: "{btn:scan-now} is how you start a scan whenever you want fresh results. You will find it in the **Scan overview** card on a banner's **Cookies & Trackers** tab. The banner header's {btn:run-scan} button starts the same scan.",
          },
          {
            id: 'ras-which-first',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'The very first scan on a banner is a **Preview** - a quick early look. Cookie Armor then runs the **Full** scan on its own right after the preview completes.',
          },
          {
            id: 'ras-which-later',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Any later {btn:scan-now} starts a **Full** scan directly - including when the only prior scan is a failed preview.',
          },
          {
            id: 'ras-which-cadence',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A completed summary also shows a **Next scan** date - the 20th of the current month, or the 20th of the next month once that date has passed.',
          },
          {
            id: 'ras-which-note',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'info',
            label: 'A full scan follows the preview',
            text: 'A completed preview is not the end of that first scan. Wait for the full scan that follows before you treat the inventory as final. While both are running, see [[why that scan stays listed|active-scans-in-drawer]].',
          },
        ],
      },
      {
        id: 'ras-empty',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What an empty tracker list can mean',
        icon: 'help-circle',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'ras-empty-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'The same empty tracker list can mean four different things, and they do not all mean the scan found nothing.',
          },
          {
            id: 'ras-empty-list',
            type: FAQ_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.GRID,
            items: [
              {
                id: 'ras-empty-completed',
                icon: 'circle-check',
                title: 'Completed and empty',
                text: 'The scan finished and observed no trackers on the pages it reached. Trackers may live only behind login, or a consent dialog the scanner could not accept may have kept gated tags from loading.',
              },
              {
                id: 'ras-empty-failed',
                icon: 'alert-triangle',
                title: 'Failed',
                text: 'The scan did not finish - do not classify from it. See [[what to do when a scan fails|fix-a-failed-scan]].',
              },
              {
                id: 'ras-empty-busy',
                icon: 'loader',
                title: 'Busy and empty',
                text: 'A scan is still running and results have not arrived yet. Wait for it to finish before you draw any conclusion.',
              },
              {
                id: 'ras-empty-filtered',
                icon: 'funnel',
                title: 'Filtered empty',
                text: 'Trackers exist on this scan, but none match the type, category, or search you applied. Clear those filters before you conclude the scan found nothing.',
              },
            ],
          },
        ],
      },
      {
        id: 'ras-limits',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What a scan sees and misses',
        icon: 'search-x',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'ras-limits-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A scan loads your site in a real browser, follows same-site links, and records cookies and web storage - **Cookie**, **Indexed DB**, **Local Storage**, and **Session Storage**. It tries common **Accept all** controls so consent-gated tags can fire, has no account credentials, and does not guess from your privacy policy.',
          },
          {
            id: 'ras-limits-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'That leaves a few gaps:',
          },
          {
            id: 'ras-limits-list',
            type: FAQ_BLOCK_TYPES.BULLETS,
            items: [
              'Pages behind a login, paywall, or any other gate the scanner cannot pass.',
              'Content that appears only after a visitor click, hover, or form submit. The scanner scrolls so lazy-loaded iframes and media can mount; it does not walk the rest of your page.',
              'A full scan visits reachable same-site pages until it finishes or hits internal crawl limits. Those limits are not a sitemap of every page on the site, and they are not published as a page count.',
            ],
          },
          {
            id: 'ras-limits-note',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'info',
            label: 'Coverage',
            text: 'When a tracker lives only on a signed-in page, point the banner at a reachable public page, or [[classify them after the full scan|classify-cookies]] from a page the scanner can reach.',
          },
        ],
      },
      {
        id: 'ras-domain',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Changing the Website Domain',
        icon: 'lock',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'ras-domain-list',
            type: FAQ_BLOCK_TYPES.BULLETS,
            items: [
              'When you change the primary **Website Domain**, confirming {btn:save-and-scan} saves the new domain and starts its scan in one step.',
              'Changing only **Additional Domains** does not start a new scan.',
              '**Website Domain** is read-only while any scan is running, including the full scan that follows a preview. It unlocks once the scan finishes.',
            ],
          },
        ],
      },
      {
        id: 'ras-history',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Reading past scans',
        icon: 'history',
        color: FAQ_SECTION_ICON_TONES.CYAN,
        blocks: [
          {
            id: 'ras-history-list',
            type: FAQ_BLOCK_TYPES.BULLETS,
            items: [
              '**View last results** opens the latest **completed** or **failed** row - not a scan that is running now.',
              'The **Pages** column in **Scan History** shows how many same-site URLs that scan visited. Live progress can show a larger discovered count while the scanner is still finding links. Neither number is a sitemap of the site.',
              'The **Trackers** column in **Scan History** shows how many trackers that scan detected and how many you have classified. Current filters do not change those numbers.',
            ],
          },
        ],
      },
    ],
  },

  'active-scans-in-drawer': {
    id: 'active-scans-in-drawer',
    title: 'Why Active Scans stays visible',
    description:
      'Active Scans keeps one row per site through the preview-to-full chain; dismiss hides the indicator and does not cancel the crawl.',
    keywords: [
      'active scans',
      'drawer',
      'scan progress',
      'scanning indicator',
      'sidebar scan',
      'dismiss scan',
      'cancel scan',
      'scan still running',
      'preview finished',
      'one scan per site',
      'already scanning',
      '409',
    ],
    lastUpdated: '2026-08-30',
    content: [
      {
        id: 'asd-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: '**Active Scans** is an enrollment list, not a second copy of scan progress. A site stays listed through the preview-to-full chain so you do not treat a finished preview as the end of the job.',
      },
      {
        id: 'asd-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'A crawl that is already running can enroll a row even if you did not start {btn:scan-now} in this session. Dismissing a row hides the indicator; it does not stop the crawl.',
      },
      {
        id: 'asd-enroll',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Why a finished preview still appears',
        icon: 'eye',
        color: FAQ_SECTION_ICON_TONES.CYAN,
        blocks: [
          {
            id: 'asd-enroll-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'When {btn:scan-now} starts a preview because the banner has no latest record, the backend starts a full scan after that preview completes. Cookie Armor keeps the same **Active Scans** row enrolled through both legs.',
          },
          {
            id: 'asd-enroll-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A completed preview is still treated as active until the chained full scan completes or fails. Starting a scan and judging results: [[When you can trust a scan|run-a-scan]].',
          },
        ],
      },
      {
        id: 'asd-concurrency',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'One scan per site',
        icon: 'store',
        color: FAQ_SECTION_ICON_TONES.CYAN,
        blocks: [
          {
            id: 'asd-concurrency-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Each site has one **Active Scans** row, and only one scan runs on that site at a time. If you trigger {btn:scan-now} while a crawl is already running, Cookie Armor keeps the running scan and re-enrolls that site if needed.',
          },
          {
            id: 'asd-concurrency-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Different sites can scan in parallel - each gets its own row.',
          },
        ],
      },
      {
        id: 'asd-dismiss',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Dismiss is not cancel',
        icon: 'x-icon',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'asd-dismiss-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'There is no cancel-scan action in the admin app. Dismiss removes the **Active Scans** indicator for that site. The crawl itself is unchanged.',
          },
          {
            id: 'asd-dismiss-list',
            type: FAQ_BLOCK_TYPES.BULLETS,
            items: [
              'The dismiss control is hidden while the row is still active, including the chained full scan after a preview.',
              'Opening the banner from a finished or failed row dismisses that indicator. An active row stays listed when you navigate.',
              'Dismissing a finished or failed row does not delete Scan History or the scan record.',
            ],
          },
        ],
      },
      {
        id: 'asd-persist',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Why the list survives a refresh',
        icon: 'database',
        color: FAQ_SECTION_ICON_TONES.CYAN,
        blocks: [
          {
            id: 'asd-persist-list',
            type: FAQ_BLOCK_TYPES.BULLETS,
            items: [
              {
                id: 'asd-persist-restore',
                text: 'Enrollment is stored for this account in the browser and restored when you return.',
              },
              {
                id: 'asd-persist-stale',
                text: 'Completed rows older than 24 hours are dropped on restore.',
              },
              {
                id: 'asd-persist-missing',
                text: 'A site that no longer exists is dropped.',
              },
              {
                id: 'asd-persist-retry',
                text: 'A temporary network error keeps the row so polling can retry.',
              },
            ],
          },
          {
            id: 'asd-persist-note',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'alert-triangle',
            label: 'Fetch failure',
            text: 'If the admin cannot refresh scan status, the row can flip to failed even when the crawler is still running or already finished. That is a fetch problem, not proof the crawl died. [[Fix a failed scan|fix-a-failed-scan]]',
          },
        ],
      },
    ],
  },

  'classify-cookies': {
    id: 'classify-cookies',
    title: 'Override a tracker category',
    description:
      'Change a tracker’s category by type, name, and domain on the current scan; preview rows stay read-only until the full scan completes.',
    keywords: [
      'classify cookies',
      'classify trackers',
      'override a tracker category',
      'change category',
      'override classification',
      'manual override',
      'reclassify',
      'cookie name',
      'indexed db',
      'local storage',
      'session storage',
      'type name domain',
      'same name different domain',
      'preview read only',
      'unclassified option',
      'cookies and trackers',
    ],
    lastUpdated: '2026-08-30',
    content: [
      {
        id: 'cc-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'An override assigns a category to one tracker on the **current** scan. Cookie Armor identifies that row by **type + name + domain**, so a Cookie and a Local Storage key that share a name, or two cookies of the same name on different hosts, stay separate.',
      },
      {
        id: 'cc-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Category meanings: [[Cookie categories|cookie-categories]]. An unclassified tracker stays allowed: [[Unclassified cookies|unclassified-cookies]].',
      },
      {
        id: 'cc-video-section',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'See a scan and classification',
        icon: 'file-video',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'cc-video-lead',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A short walkthrough of running a scan and classifying trackers.',
          },
          {
            id: 'cc-video',
            type: FAQ_BLOCK_TYPES.VIDEO,
            videoId: '1223430871',
            hash: 'cb3bf9f177',
          },
        ],
      },
      {
        id: 'cc-identity',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Type, name, and domain',
        icon: 'cookie',
        color: FAQ_SECTION_ICON_TONES.CYAN,
        blocks: [
          {
            id: 'cc-identity-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'The override is stored as that tracker’s **type** (Cookie, Indexed DB, Local Storage, or Session Storage), **name**, and **domain** on this scan.',
          },
          {
            id: 'cc-identity-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Changing the category for a cookie named `_ga` on `example.com` does not change `_ga` on another host, and it does not change a Local Storage key of the same name.',
          },
        ],
      },
      {
        id: 'cc-preview',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Preview rows stay read-only',
        icon: 'eye',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'cc-preview-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'While a preview is showing because the chained full scan is still running, **Category** is disabled. Writing an override then would attach it to the live full-scan record.',
          },
          {
            id: 'cc-preview-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Wait until that full scan completes. When a preview is incomplete: [[When you can trust a scan|run-a-scan]].',
          },
        ],
      },
      {
        id: 'cc-save',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What an override writes',
        icon: 'pencil',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'cc-save-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'After a full scan completes, change **Category** to **Analytics**, **Marketing**, **Preferences**, **Necessary**, or **Unclassified**. **Unclassified** is a real choice - use it to revert a category on this result.',
          },
          {
            id: 'cc-save-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Type, category, and search filters can hide a row that is still on the scan - [[When you can trust a scan|run-a-scan]].',
          },
          {
            id: 'cc-save-steps',
            type: FAQ_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'cc-s1',
                title: 'Wait for the full scan',
                text: 'Do not edit preview rows. **Category** unlocks when the current full scan has completed.',
              },
              {
                id: 'cc-s2',
                title: 'Set the category for that tracker',
                text: 'Choose the category on the row whose **type, name, and domain** match the tracker you mean. The current scan updates immediately, including its history for this scan.',
              },
            ],
          },
          {
            id: 'cc-save-note',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'info',
            label: 'This scan only',
            text: 'The override is saved on the current scan. Cookie Armor does not document whether a later scan keeps that choice. Developers can also pin a category in HTML with `data-cookiearmor-category` - [[Annotate your scripts|annotate-your-scripts]].',
          },
        ],
      },
    ],
  },

  'unclassified-cookies': {
    id: 'unclassified-cookies',
    title: 'Unclassified cookies',
    description:
      'An unclassified cookie or other detected tracker is allowed, not blocked. Necessary is not a way to hide an unknown tracker.',
    keywords: [
      'unclassified',
      'unclassified allowed',
      'unclassified not blocked',
      'fail open',
      'scripts not blocked',
      'unknown cookie',
      'unknown tracker',
      'needs classification',
      'compliance gap',
      'unclassified cookies',
      'unclassified trackers',
      'necessary mute',
    ],
    lastUpdated: '2026-08-30',
    content: [
      {
        id: 'uc-legal',
        type: FAQ_BLOCK_TYPES.NOTE,
        icon: 'shield',
        label: 'Not legal advice',
        text: 'Cookie Armor does not give legal advice. Classification is a product control that feeds blocking. Your counsel decides what your regulations require.',
      },
      {
        id: 'uc-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: '**An unclassified tracker is allowed, not blocked.** Cookie Armor does not withhold a cookie, Indexed DB entry, Local Storage key, or Session Storage key until someone picks a category.',
      },
      {
        id: 'uc-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'That item and the script, iframe, or pixel that set it can run for visitors who have not consented.',
      },
      {
        id: 'uc-why',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Why unclassified stays allowed',
        icon: 'circle-off',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'uc-why-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Blocking without a category would mean blocking unknown scripts. That can break checkout, login, or other first-party behaviour the scanner could not name.',
          },
          {
            id: 'uc-why-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Cookie Armor therefore blocks only **classified** scripts, iframes, and pixels whose category requires consent. Unclassified items stay allowed on purpose - fail-open, not a legal exemption.',
          },
          {
            id: 'uc-why-note',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'alert-triangle',
            label: 'Work remaining',
            text: 'Leaving trackers **Unclassified** is the most common way a scan looks finished while they still run. Treat every unclassified row as work remaining, not as a safe default. What blocking covers: [[How blocking works|how-blocking-works]].',
          },
        ],
      },
      {
        id: 'uc-necessary',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Necessary is not a mute button',
        icon: 'megaphone',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'uc-necessary-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Do not park an unknown tracker on **Necessary** to hide the unclassified label. **Necessary** is a category visitors see as required for the site to function.',
          },
          {
            id: 'uc-necessary-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'If you are unsure, read [[Cookie categories|cookie-categories]] and involve your counsel. To assign a real category: [[Override a tracker category|classify-cookies]].',
          },
        ],
      },
    ],
  },

  'fix-a-failed-scan': {
    id: 'fix-a-failed-scan',
    title: 'Fix a failed scan',
    description:
      'Match the raw scan error to a cause, see when a failed scan is tried again on its own, and when a scan-failed email is sent.',
    keywords: [
      'scan failed',
      'scan failure',
      'scan error',
      'scan timed out',
      'timeout',
      'bot protection',
      'WAF',
      'Cloudflare',
      'Turnstile',
      'CSP',
      'Content Security Policy',
      'site unreachable',
      'DNS',
      'redirect',
      'raw error message',
      'poll error',
      'fetch failed',
      'partial scan',
      'scan failed email',
      'retry',
      '30 minutes',
      '20th',
    ],
    lastUpdated: '2026-09-24',
    content: [
      {
        id: 'ffs-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'A **Failed** scan means Cookie Armor could not finish crawling the banner URL. Use the **Raw error message** to match a cause and fix that cause. Cookie Armor tries the scan again on its own only before the first successful **Full** scan. After that, start the next crawl with {btn:scan-now}.',
      },
      {
        id: 'ffs-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'An empty completed scan, a busy empty table, and a filtered empty table are not failures - [[When you can trust a scan|run-a-scan]].',
      },
      {
        id: 'ffs-retry',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'When a failed scan is tried again',
        icon: 'clock',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'ffs-retry-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Before a site has completed a **Full** scan, a failed **Preview** or a failed first **Full** scan is tried again on its own: 30 minutes after the failure, then about a day later. After that second try, Cookie Armor stops. Once a **Full** scan has completed, a later failure stays failed until you start {btn:scan-now}, or until the scheduled scan on the 20th of the month.',
          },
          {
            id: 'ffs-retry-table',
            type: FAQ_BLOCK_TYPES.TABLE,
            headers: ['Scan', 'Tried again on its own?'],
            rows: [
              [
                'Preview, started when the site is created',
                'Yes. 30 minutes later, then about a day later.',
              ],
              ['First full scan, while no full scan has completed', 'Yes. Same two tries.'],
              [
                'A later scan, after a full scan has completed',
                'No. The next automatic scan is on the 20th of the month.',
              ],
              ['Scan now, after a full scan has completed', 'No. That run is the one you started.'],
            ],
          },
          {
            id: 'ffs-retry-email',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'mail',
            label: 'Scan-failed email',
            text: 'Only a failed **Full** scan sends the scan-failed email. A failed **Preview** does not. A new site whose first full scan fails through both automatic tries can receive a few of those emails over about a day. No email is sent because another try is waiting, or because the automatic tries have stopped.',
          },
        ],
      },
      {
        id: 'ffs-not',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Failed is not empty, busy, or partial',
        icon: 'alert-triangle',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'ffs-not-list',
            type: FAQ_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.CHECKLIST,
            title: 'Not a failed scan',
            items: [
              {
                id: 'ffs-not-completed',
                text: '**Completed and empty** - The crawl finished and observed no cookies on reached pages. Retry only if you expected cookies on public pages the scanner can reach.',
              },
              {
                id: 'ffs-not-busy',
                text: '**Busy and empty** - Rows have not landed yet. Wait for the current scan; do not treat this as a failure.',
              },
              {
                id: 'ffs-not-filtered',
                text: '**Filtered empty** - Trackers exist on this scan, but none match the type, category, or search filter.',
              },
            ],
          },
          {
            id: 'ffs-not-partial',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Cookie Armor does not show a **Partial** status. Deep pages that error or time out can be skipped while the scan still completes.',
          },
          {
            id: 'ffs-not-note',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'alert-triangle',
            label: 'Fetch versus crawl',
            text: 'If the admin cannot refresh scan status while a crawl is still marked active, history can flip to **Failed**. That is a client poll or fetch failure, not proof the crawler died. Check the raw error and retry {btn:scan-now} when the network is healthy.',
          },
        ],
      },
      {
        id: 'ffs-checks',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Match the raw error',
        icon: 'search',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'ffs-checks-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Most crawl failures are the homepage never loading as a real page. Content Security Policy (CSP) is not the usual cause.',
          },
          {
            id: 'ffs-table',
            type: FAQ_BLOCK_TYPES.TABLE,
            headers: ['Cause', 'What you see', 'What to do'],
            rows: [
              [
                'Site unreachable',
                'The homepage never loaded: DNS failure, connection refused, TLS errors, or a blank homepage with no content. The whole scan fails when the first page fails.',
                'Open the banner URL in a browser from a clean network. Fix DNS, hosting, or HTTPS until that URL returns your real homepage. Cookie Armor already retries www vs non-www once when DNS fails.',
              ],
              [
                'Bot protection or WAF',
                'The homepage is a challenge page (Cloudflare Turnstile and similar) instead of your site. Cookie Armor retries automatically; if every attempt still gets a challenge, the scan fails.',
                'Allow Cookie Armor’s crawler through bot protection and your web application firewall (WAF). A challenge that only bots see will always fail the scan, because the scanner never reaches your real cookies.',
              ],
              [
                'Timeouts',
                '**Raw error message** like **Scan timed out after … minutes**, or a homepage navigation that never finished. Deep pages that time out are skipped; a homepage timeout fails the scan.',
                'Check that the site responds promptly without hanging on the first paint. Very large or slow sites take longer. Retry when the site is healthy.',
              ],
              [
                'Redirects',
                'The URL sends the crawler to a login wall, parking page, or a different host than the site you meant to scan.',
                'Set the banner URL to a public page that stays on your site. The crawler follows the homepage redirect, then only follows same-site links from there.',
              ],
              [
                'Content Security Policy (CSP)',
                'Uncommon. The scanner strips document CSP headers when they would block its own page inspection, then flags that the page ran under a weaker policy. CSP is not the default explanation for a failed scan.',
                'If the raw error still points at script injection or a policy that blocks the crawler, start at [[Content Security Policy|content-security-policy]]. Do not change CSP as a first guess.',
              ],
            ],
            caption: 'Failure modes ordered by how often they explain a real failed crawl.',
          },
        ],
      },
      {
        id: 'ffs-fix',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Retry after you fix the cause',
        icon: 'refresh-cw',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'ffs-fix-steps',
            type: FAQ_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'ffs-s1',
                title: 'Fix the matching cause',
                text: 'Use the raw error and the table above. Confirm the banner URL loads your real homepage without a challenge or login.',
              },
              {
                id: 'ffs-s2',
                title: 'Start the scan again',
                text: 'Click {btn:scan-now}. A failed preview is already a latest record, so this retry is a full scan - [[When you can trust a scan|run-a-scan]].',
              },
              {
                id: 'ffs-s3',
                title: 'Still stuck',
                text: '[[Contact support|contact-support]] with the banner URL and the **Raw error message**.',
              },
            ],
          },
          {
            id: 'ffs-email',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'You can get an email when a scan fails. [[Update email preferences|update-email-preferences]]',
          },
        ],
      },
    ],
  },
}
